import Image from "next/image";
import {
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Icon,
  Box,
  Divider,
  Center,
  SimpleGrid,
} from "@chakra-ui/react";
import { BsShieldFillCheck } from "react-icons/bs";
import { HiOutlineExternalLink } from "react-icons/hi";

// Types
import Props from "./AuditInfoModal.type";

// Constants
import {
  HALBORN_AUDIT_LINK,
  PECKSHIELD_AUDIT_LINK,
  IMMUNEFI_LINK,
  CERTORA_LINK,
  FORTA_LINK,
} from "constants/contracts";
import {
  VIEW_AUDIT_REPORT,
  VIEW_BUG_BOUNTY,
  VIEW_SECURITY,
} from "constants/analytics";

// Hooks
import useApp from "hooks/useApp";

// Images
import halbornIcon from "assets/svg/halborn.svg";
import peckshieldIcon from "assets/svg/peckshield.svg";
import certoraIcon from "assets/svg/certora.svg";
import fortaIcon from "assets/svg/forta.svg";
import immunefiIcon from "assets/svg/immunefi.svg";

const AuditModal = ({ isOpen }: Props) => {
  const { closeModal, track } = useApp();

  return (
    <Modal isOpen={isOpen} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent
        w="394px"
        bg="brand.backgroundLight"
        m={{ base: "3rem 1rem", lg: "3rem" }}
      >
        <ModalHeader fontSize="1rem" fontFamily="medium" fontWeight="500">
          Safe & secured
        </ModalHeader>
        <ModalCloseButton
          _focus={{
            boxShadow: "none",
          }}
        />
        <ModalBody pt="0" mb="1rem">
          <Center
            flexDir="column"
            mt="1rem"
            borderRadius="0.5rem"
            fontFamily="regular"
          >
            <Box
              fontSize="5rem"
              mb="1rem"
              color="brand.primary"
              _dark={{
                color: "brand.textColor",
              }}
            >
              <BsShieldFillCheck />
            </Box>
            <Text>Transparent & Regular Reviews</Text>
            <Text>Audited by Top Firms</Text>

            <Center mt="1rem" gap="1rem" w="100%">
              <SimpleGrid columns={2} w="100%" gap="0.5rem">
                <Flex
                  w="100%"
                  as="a"
                  p="1rem"
                  bg="brand.background"
                  justify="space-between"
                  cursor="pointer"
                  href={HALBORN_AUDIT_LINK}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    track(VIEW_AUDIT_REPORT, { audit_type: "HALBORN" })
                  }
                  alignItems="center"
                  borderRadius="0.5rem"
                >
                  <Flex align="center" gap="0.5rem">
                    <Image src={halbornIcon} alt="halborn-icon" />
                    <Text fontSize={12}>Halborn</Text>
                  </Flex>
                  <Icon as={HiOutlineExternalLink} color="brand.primary" />
                </Flex>
                <Flex
                  w="100%"
                  as="a"
                  cursor="pointer"
                  p="1rem"
                  bg="brand.background"
                  justify="space-between"
                  href={PECKSHIELD_AUDIT_LINK}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    track(VIEW_AUDIT_REPORT, { audit_type: "PECKSHIELD" })
                  }
                  alignItems="center"
                  borderRadius="0.5rem"
                >
                  <Flex align="center" gap="0.5rem">
                    <Image
                      src={peckshieldIcon}
                      alt="peckshield-icon"
                      width={20}
                      height={20}
                    />
                    <Text fontSize={12}>Peckshield</Text>
                  </Flex>
                  <Icon as={HiOutlineExternalLink} color="brand.primary" />
                </Flex>
                <Flex
                  w="100%"
                  as="a"
                  p="1rem"
                  bg="brand.background"
                  justify="space-between"
                  cursor="pointer"
                  href={CERTORA_LINK}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    track(VIEW_SECURITY, { audit_type: "CERTORA" })
                  }
                  alignItems="center"
                  borderRadius="0.5rem"
                >
                  <Flex align="center" gap="0.5rem">
                    <Image src={certoraIcon} alt="certora-icon" />
                    <Box w="80%">
                      <Text fontSize={12}>Certora</Text>
                      <Text fontSize={10} color="brand.textInactive">
                        Smart Contract Testing Partner
                      </Text>
                    </Box>
                  </Flex>
                  <Icon as={HiOutlineExternalLink} color="brand.primary" />
                </Flex>
                <Flex
                  w="100%"
                  as="a"
                  cursor="pointer"
                  p="1rem"
                  bg="brand.background"
                  justify="space-between"
                  href={FORTA_LINK}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    track(VIEW_SECURITY, { security_type: "FORTA" })
                  }
                  alignItems="center"
                  borderRadius="0.5rem"
                >
                  <Flex align="center" gap="0.5rem">
                    <Image src={fortaIcon} alt="forta-icon" />
                    <Box w="80%">
                      <Text fontSize={12}>Forta</Text>
                      <Text fontSize={10} color="brand.textInactive">
                        On-chain tracking partner
                      </Text>
                    </Box>
                  </Flex>
                  <Icon as={HiOutlineExternalLink} color="brand.primary" />
                </Flex>
              </SimpleGrid>
            </Center>
            <Divider borderColor="brand.separator" my="1.5rem" />
            <Center gap="0.5rem" flexDir="column">
              <Text>Report bug and earn bounty</Text>
              <Flex
                w="169px"
                as="a"
                cursor="pointer"
                p="1rem"
                bg="brand.background"
                href={IMMUNEFI_LINK}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  track(VIEW_BUG_BOUNTY, { bounty_type: "IMMUNEFI" })
                }
                alignItems="center"
                borderRadius="0.5rem"
                justify="space-between"
              >
                <Flex align="center" gap="0.5rem">
                  <Image
                    src={immunefiIcon}
                    alt="peckshield-icon"
                    width={20}
                    height={20}
                  />
                  <Text fontSize={12}>Immunefi</Text>
                </Flex>
                <Icon as={HiOutlineExternalLink} color="brand.primary" />
              </Flex>
            </Center>
          </Center>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AuditModal;
